import { useRef, useState } from "react";
import { isCancel, isAxiosError } from "axios";
import { Label, Switch } from "@libs/zapi-ui/shadcn";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { updateFeaturePermission } from "./updateFeaturePermission";
import { toast } from "sonner";
import { updateCustomSnippetsUrl } from "@/zapi-frontend/pages/SettingsPage/services/updateCustomSnippetsUrl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { useTranslation } from "react-i18next";

type TLabsModeSwitchProps = {
  variant?: "default" | "inline";
  id?: string;
  showLabel?: boolean;
  showDescription?: boolean;
  className?: string;
};

export function LabsModeSwitch(props: TLabsModeSwitchProps) {
  const { variant = "default", id = "labs-mode", showLabel = true, showDescription = false, className = "" } = props;
  const { t } = useTranslation("common");
  const { isLabs, setMe, role } = useMeStore();
  const { closeHandler, isLabsTabOpen } = useProjectSidebarStore();
  const [isLoading, setIsLoading] = useState(false);
  const labsModeAvailable = useFeatureFlag("labs-mode");
  const isAdmin = isSystemAdmin(role);
  const abortControllerRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const customSnippetsUrlMutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: () => {
      setMe({ customSnippetsUrl: null });
    }
  });

  if (!labsModeAvailable && !isAdmin) return null;

  const handleChange = async (checked: boolean) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      await updateFeaturePermission(checked ? 1 : 0, abortControllerRef.current.signal);
      await customSnippetsUrlMutation.mutateAsync({ customSnippetsUrl: null });
      setMe({ isLabs: checked });

      if (!checked && isLabsTabOpen()) {
        closeHandler();
      }
    } catch (error) {
      if (isCancel(error)) return;
      if (isAxiosError(error) && error.response?.status === 403) {
        queryClient.invalidateQueries({ queryKey: ["organization-feature-flags"] });
        setMe({ isLabs: false });
        toast.error("Labs mode has been disabled for current organization");
        return;
      }
      console.error(error);
      toast.error(checked ? t("labsMode.enableFailed") : t("labsMode.disableFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === "inline") {
    return (
      <div className={`flex items-center justify-between ${className}`}>
        {showLabel && (
          <Label htmlFor={id} className="text-[14px] font-medium">
            {t("labsMode.title")}
          </Label>
        )}
        <Switch id={id} checked={!!isLabs} onCheckedChange={handleChange} onClick={(e) => e.stopPropagation()} />
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between p-4 border rounded-lg ${className}`}>
      <div className="space-y-1">
        {showLabel && (
          <Label htmlFor={id} className="text-[14px] font-medium">
            {t("labsMode.title")}
          </Label>
        )}
        {showDescription && (
          <p className="text-[14px] text-muted-foreground">{t("labsMode.description")}</p>
        )}
      </div>
      <Switch id={id} checked={!!isLabs} disabled={isLoading} onCheckedChange={handleChange} />
    </div>
  );
}
